import { Router } from '@angular/router';
import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RankByStorDetailReportWizard } from '../rank-by-store-detail-wizard';
import { WizardPageComponent } from 'src/app/core/wizard/wizard-page-component';

interface BadgeData {
  label: string;
  color: string;
  backgroundColor: string;
}

interface MetricData {
  heading: string;
  rank: number;
  total: number;
  badge: BadgeData;
  borderColor: string;
  dotColor: string;
  hasData: boolean;
  customText?: string;
}

interface StoreData {
  name: string;
  address: string;
  bannerLabel?: string;
  companyRankings: MetricData[];
  industryRankings: MetricData[];
}

@Component({
  selector: 'mtn-store-detail-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './store-detail-report.component.html',
  styleUrl: './store-detail-report.component.scss'
})
export class StoreDetailReportComponent extends WizardPageComponent<RankByStorDetailReportWizard> implements AfterViewInit, OnDestroy {
  key = 'store-ranking-detail';
  private originalStyles: { [key: string]: string } = {};

  storeData: StoreData = {
    name: '',
    address: '',
    companyRankings: [],
    industryRankings: []
  };

  storeUuid: string | null = null;

  constructor(private router: Router) {
    super();
    this.title = 'Store Detail Report';
    this.closeButtonText = 'Close';
  }

  onLoad() {
    this.buildStoreData();
    return super.onLoad();
  }

  ngAfterViewInit(): void {
    this.adjustWizardContainerHeight();
  }

  private buildStoreData(): void {
    const model = this.wizard?.model;
    const row = model?.store;
    const milesLabel   = model?.miles   ? `${model.miles} Miles`   : '07 Miles';
    const minutesLabel = model?.minutes ? `${model.minutes} Minutes` : '20 Minutes';

    this.storeData.name    = row?.name    || 'Store';
    this.storeData.address = row?.address || '';
    this.storeUuid         = row?.storeUuid || null;

    if (!row) return;

    const bannerLabel = row.bannerName ? `vs ${row.bannerName}` : 'vs Banner';
    this.storeData.bannerLabel = bannerLabel;

    // ── Company/Banner rankings — vs same-banner stores (primary table fields)
    // API now provides band values for banner ranks too.
    const hasBannerData = (row.nationalRank ?? 0) > 0 || (row.stateRank ?? 0) > 0;
    if (hasBannerData) {
      this.storeData.companyRankings = [
        this.buildMetric('National Rank', row.nationalRank, row.nationalRankTotal, row.nationalBand),
        this.buildMetric('State Rank',    row.stateRank,    row.stateRankTotal,    row.stateBand),
        this.buildMetric('County Rank',   row.countyRank,   row.countyRankTotal,   row.countyBand),
        this.buildMetric('CBSA Rank',     row.msaRank,      row.msaRankTotal,      row.msaBand),
        this.buildMetric(milesLabel,      row.miles,        row.milesTotal,        row.milesBand),
        this.buildMetric(minutesLabel,    row.minutes,      row.minutesTotal,      row.minutesBand),
      ];
    } else {
      this.storeData.companyRankings = [
        this.buildNoDataMetric('National Rank'), this.buildNoDataMetric('State Rank'),
        this.buildNoDataMetric('County Rank'),   this.buildNoDataMetric('CBSA Rank'),
        this.buildNoDataMetric(milesLabel),      this.buildNoDataMetric(minutesLabel),
      ];
    }

    // ── Industry rankings — vs ALL grocery stores (industry* fields with bands)
    this.storeData.industryRankings = [
      this.buildMetric('National Rank', row.industryNationalRank, row.industryNationalRankTotal, row.industryNationalBand),
      this.buildMetric('State Rank',    row.industryStateRank,    row.industryStateRankTotal,    row.industryStateBand),
      this.buildMetric('County Rank',   row.industryCountyRank,   row.industryCountyRankTotal,   row.industryCountyBand),
      this.buildMetric('CBSA Rank',     row.industryMsaRank,      row.industryMsaRankTotal,      row.industryMsaBand),
      this.buildMetric(milesLabel,      row.industryMiles,        row.industryMilesTotal,        row.industryMilesBand),
      this.buildMetric(minutesLabel,    row.industryMinutes,      row.industryMinutesTotal,      row.industryMinutesBand),
    ];
  }

  private buildMetric(heading: string, rank: number | undefined, total: number | undefined, band?: string): MetricData {
    const hasData = (rank != null && rank > 0) && band?.toUpperCase() !== 'N/A';
    const badge = hasData
      ? this.getBadgeStyle(band, rank, total)
      : { label: 'N/A', color: '#6B707D', backgroundColor: '#333F54', borderColor: '#6B707D' };
    return { heading, rank: rank ?? 0, total: total ?? 0, badge, borderColor: badge.borderColor, dotColor: badge.borderColor, hasData };
  }

  private buildNoDataMetric(heading: string): MetricData {
    const badge = { label: 'No data', color: '#6B707D', backgroundColor: '#333F54', borderColor: '#6B707D' };
    return { heading, rank: 0, total: 0, badge, borderColor: '#6B707D', dotColor: '#6B707D', hasData: false };
  }

  /**
   * Maps the API band string to display label + colour tokens.
   * Label is shown exactly as the API sends it, formatted to title-case
   * so it reads naturally (e.g. "TOP" → "Top", "AVG" → "Avg").
   * For any unknown band value the raw string is title-cased and shown as-is.
   *
   * Percentile bands (backend definition):
   *   TOP       0–1 %   → teal
   *   EXCELLENT 1–10%   → green
   *   GOOD      10–40%  → blue
   *   AVG       40–70%  → yellow
   *   BELOW     70–100% → red
   */
  private getBadgeStyle(band?: string, rank?: number, total?: number): BadgeData & { borderColor: string } {
    const pct = (rank && total && total > 0) ? (rank / total) * 100 : 101;
    const key = band?.toUpperCase();

    // ── N/A band from API → always no-data grey ──────────────────────────────
    if (key === 'N/A') {
      return { label: 'N/A', color: '#6B707D', backgroundColor: '#333F54', borderColor: '#6B707D' };
    }

    // ── Helper: format the raw band string to a human-readable label ────────
    const bandLabel = (raw: string): string => {
      const labels: Record<string, string> = {
        TOP: 'Top', EXCELLENT: 'Excellent', GOOD: 'Good',
        AVG: 'Avg', AVERAGE: 'Average',
        BELOW: 'Below', BELOW_AVERAGE: 'Below', POOR: 'Below',
      };
      return labels[raw.toUpperCase()] ??
        (raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase());
    };

    // ── TOP — dark green (deeper shade = more visual weight = highest tier) ──
    if (key === 'TOP' || (!band && pct <= 1)) {
      return { label: band ? bandLabel(band) : 'Top', color: '#4ADE80', backgroundColor: 'rgba(22, 163, 74, 0.15)', borderColor: '#16A34A' };
    }

    // ── EXCELLENT — bright green (lighter shade, one step below Top) ─────────
    if (key === 'EXCELLENT' || (!band && pct <= 10)) {
      return { label: band ? bandLabel(band) : 'Excellent', color: '#86EFAC', backgroundColor: 'rgba(34, 197, 94, 0.15)', borderColor: '#22C55E' };
    }

    // ── GOOD — blue ──────────────────────────────────────────────────────────
    if (key === 'GOOD' || (!band && pct <= 40)) {
      return { label: band ? bandLabel(band) : 'Good', color: '#93C5FD', backgroundColor: 'rgba(37, 99, 235, 0.15)', borderColor: '#2563EB' };
    }

    // ── AVG / AVERAGE — yellow ───────────────────────────────────────────────
    if (key === 'AVG' || key === 'AVERAGE' || (!band && pct <= 70)) {
      return { label: band ? bandLabel(band) : 'Average', color: '#FCD34D', backgroundColor: 'rgba(217, 119, 6, 0.15)', borderColor: '#D97706' };
    }

    // ── BELOW / BELOW_AVERAGE / POOR — red ──────────────────────────────────
    if (key === 'BELOW' || key === 'BELOW_AVERAGE' || key === 'POOR' || !band) {
      return { label: band ? bandLabel(band) : 'Below', color: '#FCA5A5', backgroundColor: 'rgba(239, 68, 68, 0.15)', borderColor: '#EF4444' };
    }

    // ── Unknown band — show raw value, grey styling ──────────────────────────
    return { label: bandLabel(band!), color: '#6B707D', backgroundColor: '#333F54', borderColor: '#6B707D' };
  }

  ngOnDestroy(): void {
    this.restoreWizardContainerHeight();
  }

  private adjustWizardContainerHeight(): void {
    const wizardContainer = document.querySelector('.wizard-container .content-container') as HTMLElement;
    if (wizardContainer) {
      this.originalStyles['maxHeight'] = wizardContainer.style.maxHeight || '';
      this.originalStyles['height'] = wizardContainer.style.height || '';

      wizardContainer.style.maxHeight = '95%';
      wizardContainer.style.height = '95%';
    }
  }

  private restoreWizardContainerHeight(): void {
    const wizardContainer = document.querySelector('.wizard-container .content-container') as HTMLElement;
    if (wizardContainer) {
      wizardContainer.style.maxHeight = this.originalStyles['maxHeight'];
      wizardContainer.style.height = this.originalStyles['height'];
    }
  }

  onShare(): void {
    if (!this.storeUuid) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/detail/store', this.storeUuid])
    );
    window.open(url, '_blank');
  }

}
